import { useState, useRef, useMemo, useEffect } from "react"
import { Route, Routes, Navigate, useLocation } from "react-router-dom"
import { useIntercom } from "react-use-intercom"
import { Topbar, Sidebar } from "~/ui-rtk/components/layout"

import Docs from "../Docs"
import Settings from "../Settings"
import SettingsProfilePage from "../Settings/ProfilePage"
import SettingsPasswordPage from "../Settings/PasswordPage"
import SettingsUsersPage from "../Settings/UsersPage"
import Connectors from "../Setup/ConnectorsPage"
import useConnect from "./connect"
import ConfirmEmail from "../ConfirmEmail"
import BudgetRecommender from "../BudgetRecommender"
import ExperimentsPage from "../Experiments"
import ExperimentResults from "../Experiments/ExperimentResults"

import {
  brandRoutes,
  DEFAULT_LOGGED_ROUTE,
  guestRoutes,
  sharedRoutes,
} from "~/ui-rtk/constants/routes"

import { useScrollRestoration } from "~/ui-rtk/shared/hooks"
import useLoadingState from "~/ui-rtk/hooks/loading-state"
import { SessionRole } from "~/api/dto/auth/session-role"
import AuthenticateConnector from "../AuthenticateConnector"
import { useAuthControllerSwitchCompanyMutation } from "~/ui-rtk/api/authApi"
import { useGetTagCategoriesQuery } from "~/ui-rtk/api/tagCategoriesApi"
import { usePermissions, withGuard } from "./usePermissions"
import BrandsHealthMindShare from "../BrandHealth/MindShare"
import RevenueIndexPage from "../Revenue"
import GoogleAnalytics from "../Revenue/GoogleAnalytics"
import RevenueShopifyPage from "../Revenue/Shopify"
import RevenueShopifyOrdersPage from "../Revenue/ShopifyOrders"
import BrandValue from "../BrandValue"
import BrandsValueOverview from "../BrandValue/Overview"
import BrandMedia from "../BrandMedia"
import PageBrandMediaOptimization from "../BrandMedia/Optimization/PageBrandMediaOptimization"
import OrganicMedia from "../OrganicMedia"
import PageBrandMediaOrganicSocialPosts from "../BrandMedia/OrganicSocialPosts/PageBrandMediaOrganicSocialPosts"
import PageBrandMediaCreativeReports from "../BrandMedia/CreativeReports/CreativeReports"
import PageBrandMediaCreativeReportsV2 from "../BrandMedia/CreativeReportsV2/CreativeReportsV2"
import PageBrandMediaCreativeTaxonomyReport from "../BrandMedia/CreativeTaxonomyReport/CreativeTaxonomyReport"
import PageBrandMediaMegaFeed from "../BrandMedia/MegaFeed/MegaFeed"
import ViewCustomReportPage from "../CustomReports/ViewCustomReport"
import CustomReportEditorPage from "../CustomReports/CustomReportEditor"
import Setup from "../Setup"
import FileDropPage from "../Setup/FileDropPage/FileDropPage"
import KeywordsPage from "../Setup/KeywordsPage/KeywordsPage"
import AudiencesPage from "../Setup/AudiencesPage"
import Audits from "../Audits"
import DestinationAuditPage from "../Audits/DestinationAuditPage"
import UtmAuditPage from "../Audits/UtmAuditPage"
import ExclusionsAuditPage from "../Audits/ExclusionsAuditPage"
import { FinancialSettingsPage } from "../Setup/FinancialSettingsPage"
import { TagManagementPage } from "../Setup/TagManagementPage"
import WithDrawer from "~/ui-rtk/components/layout/WithDrawer/WithDrawer"
import SettingsCompanyPage from "../Settings/CompanyPage/CompanyPage"
import MoneyWarnDailog from "~/ui-rtk/components/ui/common/MoneyWarnDialog/MoneyWarnDailog"
import useMoneyWarnDialog from "~/ui-rtk/hooks/money-warn-dialog"
import HomePage from "../Home/HomePage"
import HomePageV2 from "../HomeV2/HomePageV2"
import useHomepageV2Access from "~/ui-rtk/hooks/homepage-v2-access"

import { SubscriptionGuard } from "~/ui-rtk/components/features/guards/SubscriptionGuard"
import UpgradePage from "~/ui-rtk/pages/Upgrade"
import { PricingDetails } from "../PricingDetails"
import { SettingsBrandEventsPage } from "../Settings/BrandEventsPage/BrandEventsPage"
import { Confirm } from "../Subscribe/Confirm/Confirm"
import { AdhocFivetranConnection } from "../AdhocFivetranConnection"
import { RevenueSourcePage as SetupRevenueSourcePage } from "../Setup/RevenueSourcePage"
import useExperimentsAccess from "~/ui-rtk/hooks/experiment-access"
import usePostHogFeatureFlags from "~/ui-rtk/hooks/posthog-feaures"
import { POSTHOG_FEATURES } from "~/ui-rtk/constants/feature-flags"
import useAccess from "~/ui-rtk/hooks/access"
import { URL_COMPANY_ID } from "~/ui-rtk/constants/url-params"
import { getCompanySwitchTarget } from "./companySwitchTarget"
import { useURLSearchParams } from "~/ui-rtk/hooks/url"
import PageBrandMediaMediaMentions from "../BrandMedia/Mentions/PageBrandMediaMentions"
import useMediaMentionsAccess from "~/ui-rtk/hooks/media-mentions-access"
import useShopifyAccess from "~/ui-rtk/hooks/shopify-access"
import useGAConnectorConnected from "~/ui-rtk/hooks/ga-connector-connected"
import getAnnualRevenue from "~/ui-rtk/hooks/get-anunual-revenue.ts"
import { usePostHog } from "posthog-js/react"
import NewGoalPage from "../Goals/NewGoalPage"
import useCreativeReportsPageAccess from "~/ui-rtk/hooks/creative-reports-access"
import useCreativeReportsV2Access from "~/ui-rtk/hooks/creative-reports-v2-access"
import useMentionsReviewAccess from "~/ui-rtk/hooks/mentions-review-access"
import MentionsReviewPage from "../BrandMedia/MentionsReview/MentionsReview"
import KeyEventsPage from "../Settings/KeyEventsPage"
import MetricDictionaryPage from "../Settings/MetricDictionaryPage"
import PipelineMonitorPage from "../Settings/PipelineMonitorPage"
import SettingsApiKeysPage from "../Settings/ApiKeysPage"
import SettingsClaudePage from "../Settings/ClaudePage"
import BudgetUpdatesPage from "../Settings/BudgetUpdatesPage"
import AdBlasterSettingsPage from "../Settings/AdBlasterPage"
import HowAISeesYourBrand from "../BrandPositioning/HowAISeesYourBrand"
import useHowAISeesYourBrandAccess from "~/ui-rtk/hooks/how-ai-sees-your-brand-access"
import BrandResearch from "../BrandResearch"
import MentionsAnalysis from "../BrandResearch/MentionsAnalysis"
import PagePaidWeightModel from "../BrandMedia/PaidMediaWeights/PagePaidWeightModel"
import PageBrandMediaAdDestinations from "../BrandMedia/AdDestinations"
import usePaidMediaWeightsAccess from "~/ui-rtk/hooks/paid-media-weights-access"
import RevenueKPI from "../Revenue/KPI"
import useRevenueKPIAccess from "~/ui-rtk/hooks/revenue-kpi-access"
import LTVCohortsPage from "../LTVCohorts"
import SubsequentPurchasesPage from "../SubsequentPurchases"
import CollectionFunnelPage from "../WebMerchandising/CollectionFunnel"
import ProductPageFunnelPage from "../WebMerchandising/ProductPageFunnel"
import HomepageFunnelPage from "../WebMerchandising/HomepageFunnel"
import useWebMerchandisingAccess from "~/ui-rtk/hooks/web-merchandising-access"
import PagePathReportPage from "../WebAnalytics/PagePathReport"
import LandingPageReportPage from "../WebAnalytics/LandingPageReport"
import ProductMediaPnLPage from "../WebMerchandising/ProductMediaPnL"
import JourneyAnalysisPage from "../WebAnalytics/JourneyAnalysis"
import PreAtcAnalysisPage from "../WebAnalytics/PreAtcAnalysis"
import PageDepthAnalysisPage from "../WebAnalytics/PageDepthAnalysis"
import InventoryAnalysisPage from "../WebAnalytics/InventoryAnalysis"
import AdBlasterPage from "../AdBlaster"
import CampaignBlasterPage from "../CampaignBlaster"
import JobsPage from "../Jobs"
import CommentModerationPage from "../CommentModeration"
import MetaBudgetsPage from "../MetaBudgets"
import AdminSchedulesPage from "../Jobs/AdminSchedules"
import AdBlastReportsPage from "../AdBlastReports"
import BrandAgendaPage from "../BrandAgenda"
import CreativeManagementPage from "../CreativeManagement"
import useAdForgeAccess from "~/ui-rtk/hooks/ad-forge-access"
import useCreativeManagementAccess from "~/ui-rtk/hooks/creative-management-access"
import useCommentModerationAccess from "~/ui-rtk/hooks/comment-moderation-access"
import useMetaBudgetEditorAccess from "~/ui-rtk/hooks/meta-budget-editor-access"
import useMetricDictionaryAccess from "~/ui-rtk/hooks/metric-dictionary-access"
import useChatbotAccess from "~/ui-rtk/hooks/chatbot-access"
import { ChatButton } from "~/ui-rtk/components/chatbot/ChatButton"
import MarketPredictionPage from "../MarketPrediction"
import useMarketPredictionAccess from "~/ui-rtk/hooks/market-prediction-access"

const NO_LAYOUT_PAGES: string[] = []
const NO_SIDEBAR_PAGES = [guestRoutes.SUBSCRIBE_CONFIRM]
const NO_TOPBAR_PAGES: string[] = []

export default function Main() {
  const {
    currentCompany,
    sessionRole,
    isAccessGranted,
    isBillingEnabled,
    // isPermissionGranted,
    isUserManagementGranted,
    isCompanyManagementGranted,
    isBrandConversionEventsManagementGranted,
  } = usePermissions()
  const { boot, update } = useIntercom()

  const posthog = usePostHog()
  const { revenue } = getAnnualRevenue()

  const hasHowAISeesYourBrandAccess = useHowAISeesYourBrandAccess()
  const hasRevenueKPIAccess = useRevenueKPIAccess()
  const hasWebMerchandisingAccess = useWebMerchandisingAccess()

  const hasAccessToMediaMentions = useMediaMentionsAccess()
  const hasAccessToPaidMediaWeights = usePaidMediaWeightsAccess()
  const hasMentionsReviewAccess = useMentionsReviewAccess()
  const hasAccessToCreativeReports = useCreativeReportsPageAccess()
  const hasAccessToCreativeReportsV2 = useCreativeReportsV2Access()
  const hasHomepageV2Access = useHomepageV2Access()
  const [switchAsync] = useAuthControllerSwitchCompanyMutation()
  const { session, refetch } = useConnect()
  const { params, setParams } = useURLSearchParams()
  const { [URL_COMPANY_ID]: companyIdFromUrl } = params

  // Every API call is tenant-scoped by the session's currentCompany, so a
  // `_c_id` link must land the switch BEFORE any page mounts and fires
  // requests (a fire-and-forget switch here used to let early calls like
  // /assets/resolve run — and create assets — under the previous company).
  //
  // `session` is guaranteed defined on Main's first render: AuthPage only
  // mounts <Main /> inside `if (session)`, and both read `currentData` from
  // the same useAuthControllerCurrentUserQuery cache entry. If Main could
  // ever mount sessionless, the captured target would lock in `undefined`
  // and the effect below would strip `_c_id` without switching.
  //
  // Captured ONCE via a lazy initializer: the whole flow is one-shot on
  // mount, so the gate state and the mount effect must see the same target
  // even though params/session change across renders (the param is stripped
  // and the session refetched mid-flow).
  const [companySwitchTarget] = useState(() =>
    getCompanySwitchTarget(session, companyIdFromUrl),
  )
  const [isSwitchingCompany, setIsSwitchingCompany] = useState(
    Boolean(companySwitchTarget),
  )

  // Prefetch tag categories at the app shell so any table that uses
  // structured tags (TagPanel, TagsCell) renders with the catalog already
  // resolved. RTK Query dedupes — downstream `useTagCategories` hits cache.
  // Held back during a `_c_id` switch so it doesn't cache the old company's
  // catalog (the topbar switch path avoids this via a full page reload).
  useGetTagCategoriesQuery(undefined, {
    skip: !session?.currentCompany.id || isSwitchingCompany,
  })

  const { setIsLoading } = useLoadingState()
  const [renderKey, requestRerender] = useState(
    session?.currentCompany.id ?? "",
  )
  const { isSosFree } = useAccess()
  const mainFrameRef = useRef<HTMLDivElement | null>(null)
  const hasExperimentsAccess = useExperimentsAccess()
  const { hasAccess: hasAdForgeAccess } = useAdForgeAccess()
  const { hasAccess: hasCreativeManagementAccess } =
    useCreativeManagementAccess()
  const { hasAccess: hasCommentModerationAccess } = useCommentModerationAccess()
  const { hasAccess: hasMetaBudgetEditorAccess } = useMetaBudgetEditorAccess()
  const isRevenueSourcePageFeatureEnabled = usePostHogFeatureFlags(
    POSTHOG_FEATURES.REVENUE_DEP_VAR_SETUP_PAGE,
  )
  const hasMetricDictionaryAccess = useMetricDictionaryAccess()
  const hasChatbotAccess = useChatbotAccess()
  const hasMarketPredictionAccess = useMarketPredictionAccess()
  const isRevenueShopifyPageEnabled = useShopifyAccess()
  const isLtvCohortsPageEnabled = usePostHogFeatureFlags(
    POSTHOG_FEATURES.LTV_COHORTS,
  )
  const isSubsequentPurchasesPageEnabled = usePostHogFeatureFlags(
    POSTHOG_FEATURES.SUBSEQUENT_PURCHASES,
  )
  const isGAAvailable = useGAConnectorConnected()

  const location = useLocation()
  const { pathname } = location

  const noBars = useMemo(() => NO_LAYOUT_PAGES.includes(pathname), [pathname])
  const noSideBar = useMemo(
    () => NO_SIDEBAR_PAGES.includes(pathname),
    [pathname],
  )
  const noTopBar = useMemo(() => NO_TOPBAR_PAGES.includes(pathname), [pathname])

  useScrollRestoration(mainFrameRef)
  const [showMoneyWarnDialog, hideMoneyWarnDialog] = useMoneyWarnDialog()

  const { user } = session || {}

  useEffect(() => {
    boot()
  }, [boot])

  useEffect(() => {
    if (!companyIdFromUrl) {
      return
    }
    const applyCompanyFromUrl = async () => {
      if (companySwitchTarget) {
        setIsLoading(true)
        try {
          await switchAsync({
            switchCompanyDto: { companyId: companySwitchTarget },
          }).unwrap()
          await refetch()
        } catch {
          // switch failed — stay on the session's company
        } finally {
          setIsSwitchingCompany(false)
          setIsLoading(false)
        }
      }
      setParams({
        ...params,
        [URL_COMPANY_ID]: undefined,
      })
    }
    void applyCompanyFromUrl()
  }, [])

  if (!session) {
    return <Navigate to="/" replace />
  }

  useEffect(() => {
    if (user) {
      // Identify sends an event, so you want may want to limit how often you call it
      posthog?.identify(user.id, {
        email: user.email.toLowerCase(),
        name: user.name,
      })
      if (currentCompany && revenue) {
        posthog?.group("company", currentCompany.id, {
          name: currentCompany.name,
          setupStatus: currentCompany.setupStatus,
          subscription: currentCompany.subscription,
          revenue,
        })
      }

      posthog.reloadFeatureFlags()
    }
  }, [posthog, user?.id, user?.email, currentCompany?.id, revenue])

  useEffect(() => {
    if (user && currentCompany) {
      update({
        userId: user?.id,
        name: user?.name,
        email: user?.email,
        company: {
          companyId: currentCompany?.id,
          name: currentCompany?.name,
          createdAt: currentCompany?.createdAt,
          website: currentCompany?.domain,
          industry: currentCompany?.metadata?.industry,
        },
      })
    }
  }, [update, user?.id, user?.email, currentCompany?.id])

  let defaultRoute: string

  if (sessionRole === SessionRole.SUPER_ADMIN) {
    defaultRoute = DEFAULT_LOGGED_ROUTE
  } else if (isSosFree) {
    // Free Share of Search users: the report is their home (APP-453).
    defaultRoute = brandRoutes.MARKET_SHARE.SHARE_OF_SEARCH
  } else if (isBillingEnabled && isAccessGranted) {
    defaultRoute = DEFAULT_LOGGED_ROUTE
  } else if (isBillingEnabled) {
    defaultRoute = guestRoutes.UPGRADE
  } else {
    defaultRoute = brandRoutes.SETUP.CONNECTORS
  }

  if (isSwitchingCompany) {
    // Global loading overlay is up; don't mount pages until the session's
    // company matches the link's company.
    return null
  }

  const handleCompanyChange = async (companyId: string) => {
    setIsLoading(true)
    await switchAsync({ switchCompanyDto: { companyId } })
    await refetch()
    requestRerender(companyId)
    setIsLoading(false)
    window?.location?.reload()
  }

  return (
    <div
      key={renderKey}
      ref={mainFrameRef}
      className="h-full overflow-y-auto bg-basic-black text-basic-light"
    >
      {currentCompany && !noBars && !noTopBar && (
        <Topbar
          onCompanyChange={handleCompanyChange}
          currentCompany={currentCompany}
        />
      )}
      {!noBars && !noSideBar && <Sidebar />}

      <div
        className={
          noBars || noTopBar ? "p-2" : "mt-15 md:ms-16 h-[calc(100%-4rem)]"
        }
      >
        <WithDrawer>
          <Routes>
            <Route
              path={brandRoutes.HOME.ROOT}
              element={withGuard(
                <SubscriptionGuard>
                  {hasHomepageV2Access ? <HomePageV2 /> : <HomePage />}
                </SubscriptionGuard>,
                [],
              )}
            />
            <Route
              path={brandRoutes.HOME.OVERVIEW}
              element={withGuard(
                <SubscriptionGuard>
                  <HomePageV2 />
                </SubscriptionGuard>,
                [],
              )}
            />
            <Route
              path={brandRoutes.HOME.KPI_DEEP_DIVE}
              element={withGuard(
                <SubscriptionGuard>
                  <RevenueKPI />
                </SubscriptionGuard>,
                [isAccessGranted],
              )}
            />
            {/* The unified Metrics Explorer: the KPI page opening on its
                Explorer view (prop-driven default — the URL is never
                rewritten). The legacy Brand-Value-based explorer stays
                untouched at /brand-value/overview. */}
            <Route
              path={brandRoutes.HOME.METRICS_EXPLORER}
              element={withGuard(
                <SubscriptionGuard>
                  <RevenueKPI initialView="explorer" />
                </SubscriptionGuard>,
                [isAccessGranted],
              )}
            />
            <Route
              path={brandRoutes.HOME.LTV_COHORTS}
              element={withGuard(
                <SubscriptionGuard>
                  <LTVCohortsPage />
                </SubscriptionGuard>,
                [
                  isAccessGranted,
                  isRevenueShopifyPageEnabled,
                  isLtvCohortsPageEnabled,
                ],
              )}
            />
            <Route
              path={brandRoutes.HOME.SUBSEQUENT_PURCHASES}
              element={withGuard(
                <SubscriptionGuard>
                  <SubsequentPurchasesPage />
                </SubscriptionGuard>,
                [
                  isAccessGranted,
                  isRevenueShopifyPageEnabled,
                  isSubsequentPurchasesPageEnabled,
                ],
              )}
            />
            <Route
              path={brandRoutes.HOME.V2}
              element={withGuard(
                <SubscriptionGuard>
                  <HomePageV2 />
                </SubscriptionGuard>,
                [],
              )}
            />
            <Route
              path={brandRoutes.HOME.NEW_GOAL}
              element={withGuard(
                <SubscriptionGuard>
                  <NewGoalPage />
                </SubscriptionGuard>,
                [],
              )}
            />
            <Route
              path={brandRoutes.MARKET_SHARE.SHARE_OF_SEARCH}
              element={withGuard(
                <SubscriptionGuard sosFreeAllowed>
                  <BrandsHealthMindShare />
                </SubscriptionGuard>,
                [isAccessGranted],
              )}
            />
            <Route
              path={brandRoutes.REVENUE.KPI}
              element={withGuard(
                <SubscriptionGuard>
                  <RevenueKPI />
                </SubscriptionGuard>,
                [isAccessGranted, hasRevenueKPIAccess],
              )}
            />
            <Route
              path={brandRoutes.BRAND_VALUE.ROOT}
              element={withGuard(
                <SubscriptionGuard>
                  <BrandValue />
                </SubscriptionGuard>,
                [isAccessGranted],
              )}
            >
              <Route
                path={brandRoutes.BRAND_VALUE.OVERVIEW}
                element={withGuard(
                  <SubscriptionGuard>
                    <BrandsValueOverview />
                  </SubscriptionGuard>,
                  [isAccessGranted],
                )}
              />
              {/* https://linear.app/marathon-data/issue/MD-2250/hide-cohortvisualization-for-mvp */}
              {/* <Route
                path={brandRoutes.BRAND_VALUE.COHORT_ANALYSIS}
                element={withGuard(<BrandsValueCohortAnalysis />, [
                                    isPermissionGranted([
                    SessionRole.ADMIN,
                    SessionRole.USER,
                    SessionRole.SUPER_ADMIN,
                  ]),
                ])}
              /> */}
              <Route
                index
                element={
                  <Navigate to={brandRoutes.BRAND_VALUE.OVERVIEW} replace />
                }
              />
            </Route>
            <Route
              path={guestRoutes.PRICING_DETAILS}
              element={<PricingDetails />}
            />
            <Route
              path={brandRoutes.CAMPAIGN_MANAGEMENT.ROOT}
              element={withGuard(
                <SubscriptionGuard>
                  <BudgetRecommender />
                </SubscriptionGuard>,
                [isAccessGranted],
              )}
            />
            <Route
              path={brandRoutes.EXPERIMENTS.ROOT}
              element={withGuard(
                <SubscriptionGuard>
                  <ExperimentsPage />
                </SubscriptionGuard>,
                [isAccessGranted, hasExperimentsAccess],
              )}
            />
            <Route
              path={brandRoutes.EXPERIMENTS.RESULTS}
              element={withGuard(
                <SubscriptionGuard>
                  <ExperimentResults />
                </SubscriptionGuard>,
                [isAccessGranted, hasExperimentsAccess],
              )}
            />
            <Route path={guestRoutes.UPGRADE} element={<UpgradePage />} />
            <Route
              path={guestRoutes.SUBSCRIBE}
              element={<Navigate to={guestRoutes.UPGRADE} replace />}
            />
            <Route path={guestRoutes.SUBSCRIBE_CONFIRM} element={<Confirm />} />
            <Route
              path={brandRoutes.BRAND_MEDIA.ROOT}
              element={withGuard(
                <SubscriptionGuard>
                  <BrandMedia />
                </SubscriptionGuard>,
                [isAccessGranted],
              )}
            >
              <Route
                path={brandRoutes.BRAND_MEDIA.PAID_OPTIMIZATION}
                element={withGuard(
                  <SubscriptionGuard>
                    <PageBrandMediaOptimization />
                  </SubscriptionGuard>,
                  [isAccessGranted],
                )}
              />

              <Route
                index
                element={
                  <Navigate
                    to={brandRoutes.BRAND_MEDIA.PAID_OPTIMIZATION}
                    replace
                  />
                }
              />
              <Route
                path={brandRoutes.BRAND_MEDIA.PAID_WEIGHTS}
                element={withGuard(
                  <SubscriptionGuard>
                    <PagePaidWeightModel />
                  </SubscriptionGuard>,
                  [isAccessGranted, hasAccessToPaidMediaWeights],
                )}
              />
              <Route
                path={brandRoutes.BRAND_MEDIA.AD_DESTINATIONS}
                element={withGuard(
                  <SubscriptionGuard>
                    <PageBrandMediaAdDestinations />
                  </SubscriptionGuard>,
                  [isAccessGranted],
                )}
              />
            </Route>
            <Route
              path={brandRoutes.ORGANIC_MEDIA.ROOT}
              element={withGuard(
                <SubscriptionGuard>
                  <OrganicMedia />
                </SubscriptionGuard>,
                [isAccessGranted],
              )}
            >
              <Route
                path={brandRoutes.ORGANIC_MEDIA.ORGANIC_SOCIAL_POSTS}
                element={withGuard(
                  <SubscriptionGuard>
                    <PageBrandMediaOrganicSocialPosts />
                  </SubscriptionGuard>,
                  [isAccessGranted],
                )}
              />
              {hasMentionsReviewAccess ? (
                <Route
                  path={brandRoutes.ORGANIC_MEDIA.MEDIA_MENTIONS}
                  element={withGuard(
                    <SubscriptionGuard>
                      <MentionsReviewPage />
                    </SubscriptionGuard>,
                    [isAccessGranted],
                  )}
                />
              ) : (
                <Route
                  path={brandRoutes.ORGANIC_MEDIA.MEDIA_MENTIONS}
                  element={withGuard(
                    <SubscriptionGuard>
                      <PageBrandMediaMediaMentions />
                    </SubscriptionGuard>,
                    [isAccessGranted, hasAccessToMediaMentions],
                  )}
                />
              )}
              <Route
                index
                element={
                  <Navigate
                    to={brandRoutes.ORGANIC_MEDIA.ORGANIC_SOCIAL_POSTS}
                    replace
                  />
                }
              />
            </Route>
            <Route
              path={brandRoutes.CREATIVE_REPORTS.ROOT}
              element={withGuard(
                <SubscriptionGuard>
                  {hasAccessToCreativeReportsV2 ? (
                    <PageBrandMediaCreativeReportsV2 />
                  ) : (
                    <PageBrandMediaCreativeReports />
                  )}
                </SubscriptionGuard>,
                [isAccessGranted, hasAccessToCreativeReports],
              )}
            />
            <Route
              path={brandRoutes.CREATIVE_REPORTS.TAXONOMY}
              element={withGuard(
                <SubscriptionGuard>
                  <PageBrandMediaCreativeTaxonomyReport />
                </SubscriptionGuard>,
                [isAccessGranted, hasAccessToCreativeReports],
              )}
            />
            <Route
              path={brandRoutes.MEGA_FEED.ROOT}
              element={withGuard(
                <SubscriptionGuard>
                  <PageBrandMediaMegaFeed />
                </SubscriptionGuard>,
                [isAccessGranted],
              )}
            />
            <Route
              path={brandRoutes.CUSTOM_REPORTS.ROOT}
              element={<Navigate to={brandRoutes.CUSTOM_REPORTS.NEW} replace />}
            />
            <Route
              path={brandRoutes.CUSTOM_REPORTS.NEW}
              element={withGuard(
                <SubscriptionGuard>
                  <CustomReportEditorPage />
                </SubscriptionGuard>,
                [isAccessGranted],
              )}
            />
            <Route
              path={brandRoutes.CUSTOM_REPORTS.EDIT}
              element={withGuard(
                <SubscriptionGuard>
                  <CustomReportEditorPage />
                </SubscriptionGuard>,
                [isAccessGranted],
              )}
            />
            <Route
              path={brandRoutes.CUSTOM_REPORTS.VIEW}
              element={withGuard(
                <SubscriptionGuard>
                  <ViewCustomReportPage />
                </SubscriptionGuard>,
                [isAccessGranted],
              )}
            />
            <Route
              path={brandRoutes.BRAND_POSITIONING.ROOT}
              element={withGuard(
                <SubscriptionGuard>
                  <HowAISeesYourBrand />
                </SubscriptionGuard>,
                [isAccessGranted, hasHowAISeesYourBrandAccess],
              )}
            />
            <Route
              path={brandRoutes.BRAND_RESEARCH.ROOT}
              element={withGuard(
                <SubscriptionGuard>
                  <BrandResearch />
                </SubscriptionGuard>,
                [isAccessGranted],
              )}
            >
              <Route
                path={brandRoutes.BRAND_RESEARCH.MENTIONS_ANALYSIS}
                element={withGuard(
                  <SubscriptionGuard>
                    <MentionsAnalysis />
                  </SubscriptionGuard>,
                  [isAccessGranted],
                )}
              />
              <Route
                index
                element={
                  <Navigate
                    to={brandRoutes.BRAND_RESEARCH.MENTIONS_ANALYSIS}
                    replace
                  />
                }
              />
            </Route>
            <Route
              path={brandRoutes.WEB_MERCHANDISING.COLLECTION_FUNNEL}
              element={withGuard(
                <SubscriptionGuard>
                  <CollectionFunnelPage />
                </SubscriptionGuard>,
                [isAccessGranted, hasWebMerchandisingAccess],
              )}
            />
            <Route
              path={brandRoutes.WEB_MERCHANDISING.PRODUCT_PAGE_FUNNEL}
              element={withGuard(
                <SubscriptionGuard>
                  <ProductPageFunnelPage />
                </SubscriptionGuard>,
                [isAccessGranted, hasWebMerchandisingAccess],
              )}
            />
            <Route
              path={brandRoutes.WEB_MERCHANDISING.HOMEPAGE_FUNNEL}
              element={withGuard(
                <SubscriptionGuard>
                  <HomepageFunnelPage />
                </SubscriptionGuard>,
                [isAccessGranted, hasWebMerchandisingAccess],
              )}
            />
            <Route
              path={brandRoutes.REVENUE.ROOT}
              element={withGuard(
                <SubscriptionGuard>
                  <RevenueIndexPage />
                </SubscriptionGuard>,
                [isAccessGranted],
              )}
            >
              <Route
                path={brandRoutes.REVENUE.GOOGLE_ANALYTICS}
                element={withGuard(
                  <SubscriptionGuard>
                    <GoogleAnalytics />
                  </SubscriptionGuard>,
                  [isAccessGranted, isGAAvailable],
                )}
              />
              <Route
                path={brandRoutes.REVENUE.SHOPIFY}
                element={withGuard(
                  <SubscriptionGuard>
                    <RevenueShopifyPage />
                  </SubscriptionGuard>,
                  [isAccessGranted, isRevenueShopifyPageEnabled],
                )}
              />
              <Route
                path={brandRoutes.REVENUE.SHOPIFY_ORDERS}
                element={withGuard(
                  <SubscriptionGuard>
                    <RevenueShopifyOrdersPage />
                  </SubscriptionGuard>,
                  [isAccessGranted, isRevenueShopifyPageEnabled],
                )}
              />
              <Route
                index
                element={
                  <Navigate to={brandRoutes.REVENUE.GOOGLE_ANALYTICS} replace />
                }
              />
            </Route>
            <Route
              path={brandRoutes.WEB_MERCHANDISING.PAGE_PATH_REPORT}
              element={withGuard(
                <SubscriptionGuard>
                  <PagePathReportPage />
                </SubscriptionGuard>,
                [isAccessGranted, hasWebMerchandisingAccess],
              )}
            />
            <Route
              path={brandRoutes.WEB_MERCHANDISING.LANDING_PAGE_REPORT}
              element={withGuard(
                <SubscriptionGuard>
                  <LandingPageReportPage />
                </SubscriptionGuard>,
                [isAccessGranted, hasWebMerchandisingAccess],
              )}
            />
            <Route
              path={brandRoutes.WEB_MERCHANDISING.PRODUCT_MEDIA_PNL}
              element={withGuard(
                <SubscriptionGuard>
                  <ProductMediaPnLPage />
                </SubscriptionGuard>,
                [isAccessGranted, hasWebMerchandisingAccess],
              )}
            />
            <Route
              path={brandRoutes.WEB_MERCHANDISING.PRE_ATC_ANALYSIS}
              element={withGuard(
                <SubscriptionGuard>
                  <PreAtcAnalysisPage />
                </SubscriptionGuard>,
                [isAccessGranted, hasWebMerchandisingAccess],
              )}
            />
            <Route
              path={brandRoutes.WEB_MERCHANDISING.PAGE_DEPTH_ANALYSIS}
              element={withGuard(
                <SubscriptionGuard>
                  <PageDepthAnalysisPage />
                </SubscriptionGuard>,
                [isAccessGranted, hasWebMerchandisingAccess],
              )}
            />
            <Route
              path={brandRoutes.WEB_MERCHANDISING.INVENTORY_ANALYSIS}
              element={withGuard(
                <SubscriptionGuard>
                  <InventoryAnalysisPage />
                </SubscriptionGuard>,
                [isAccessGranted, hasWebMerchandisingAccess],
              )}
            />
            <Route
              path={brandRoutes.WEB_ANALYTICS.JOURNEY_ANALYSIS}
              element={withGuard(
                <SubscriptionGuard>
                  <JourneyAnalysisPage />
                </SubscriptionGuard>,
                [isAccessGranted, hasWebMerchandisingAccess],
              )}
            />
            {/* Setup (connectors, keywords, audiences…) is deliberately
                unguarded for paid companies — it's their onboarding surface
                and the no-billing defaultRoute target. Free SoS companies
                must not reach it (clicking "connect" would create real
                Fivetran/Airbyte connections), so they see the upsell
                instead (APP-453). */}
            <Route
              path={brandRoutes.SETUP.ROOT}
              element={isSosFree ? <UpgradePage /> : <Setup />}
            >
              <Route
                path={brandRoutes.SETUP.CONNECTORS}
                element={<Connectors />}
              />
              <Route
                path={brandRoutes.SETUP.FILE_DROP}
                element={<FileDropPage />}
              />
              <Route
                path={brandRoutes.SETUP.KEYWORDS}
                element={<KeywordsPage />}
              />
              <Route
                path={brandRoutes.SETUP.AUDIENCES}
                element={<AudiencesPage />}
              />
              {/* Moved to the Audits section; redirect keeps old links alive. */}
              <Route
                path={brandRoutes.SETUP.EXCLUSIONS_AUDIT}
                element={
                  <Navigate to={brandRoutes.AUDITS.EXCLUSIONS} replace />
                }
              />
              <Route
                path={brandRoutes.SETUP.REVENUE_SOURCE}
                element={withGuard(<SetupRevenueSourcePage />, [
                  isRevenueSourcePageFeatureEnabled,
                ])}
              />
              <Route
                path={brandRoutes.SETUP.FINANCIAL_SETTINGS}
                element={<FinancialSettingsPage />}
              />
              <Route
                path={brandRoutes.SETUP.TAGS}
                element={<TagManagementPage />}
              />
              <Route
                path={brandRoutes.SETUP.METRIC_DICTIONARY}
                element={withGuard(<MetricDictionaryPage />, [
                  hasMetricDictionaryAccess,
                ])}
              />
              {/* Moved to the Audits section; redirect keeps old links alive. */}
              <Route
                path={brandRoutes.SETUP.UTM_AUDIT}
                element={<Navigate to={brandRoutes.AUDITS.UTM} replace />}
              />
              <Route
                index
                element={<Navigate to={brandRoutes.SETUP.CONNECTORS} replace />}
              />
            </Route>
            <Route path={brandRoutes.AUDITS.ROOT} element={<Audits />}>
              <Route
                path={brandRoutes.AUDITS.DESTINATION_URLS}
                element={<DestinationAuditPage />}
              />
              <Route path={brandRoutes.AUDITS.UTM} element={<UtmAuditPage />} />
              <Route
                path={brandRoutes.AUDITS.EXCLUSIONS}
                element={<ExclusionsAuditPage />}
              />
              <Route
                index
                element={
                  <Navigate to={brandRoutes.AUDITS.DESTINATION_URLS} replace />
                }
              />
            </Route>
            <Route path={brandRoutes.SETTINGS.ROOT} element={<Settings />}>
              <Route
                path={brandRoutes.SETTINGS.PROFILE}
                element={<SettingsProfilePage />}
              />
              <Route
                path={brandRoutes.SETTINGS.PASSWORD}
                element={<SettingsPasswordPage />}
              />
              <Route
                path={brandRoutes.SETTINGS.KEY_EVENTS}
                element={isSosFree ? <UpgradePage /> : <KeyEventsPage />}
              />
              <Route
                path={brandRoutes.SETTINGS.USERS}
                element={withGuard(
                  <SettingsUsersPage />,
                  isUserManagementGranted,
                )}
              />
              <Route
                path={brandRoutes.SETTINGS.COMPANY}
                element={withGuard(
                  <SettingsCompanyPage />,
                  isCompanyManagementGranted,
                )}
              />
              <Route
                path={brandRoutes.SETTINGS.BRAND_EVENTS}
                element={withGuard(
                  isSosFree ? <UpgradePage /> : <SettingsBrandEventsPage />,
                  isBrandConversionEventsManagementGranted,
                )}
              />
              <Route
                path={brandRoutes.SETTINGS.PIPELINE_MONITOR}
                element={withGuard(
                  isSosFree ? <UpgradePage /> : <PipelineMonitorPage />,
                  isCompanyManagementGranted,
                )}
              />
              {/* Platform-tier settings (CLI keys, budget ops, KPI/event
                  config, pipeline monitor) are paywalled for free SoS
                  companies — account-level tabs (profile, password, users,
                  company) stay reachable (APP-453). */}
              <Route
                path={brandRoutes.SETTINGS.API_KEYS}
                element={isSosFree ? <UpgradePage /> : <SettingsApiKeysPage />}
              />
              <Route
                path={brandRoutes.SETTINGS.CLAUDE}
                element={isSosFree ? <UpgradePage /> : <SettingsClaudePage />}
              />
              <Route
                path={brandRoutes.SETTINGS.BUDGET_UPDATES}
                element={isSosFree ? <UpgradePage /> : <BudgetUpdatesPage />}
              />
              <Route
                path={brandRoutes.SETTINGS.AD_BLASTER}
                element={
                  isSosFree ? <UpgradePage /> : <AdBlasterSettingsPage />
                }
              />
              <Route
                index
                element={<Navigate to={brandRoutes.SETTINGS.PROFILE} replace />}
              />
            </Route>
            <Route
              path={brandRoutes.AUTHENTICATE_CONNECTOR}
              element={
                <AuthenticateConnector
                  redirectTo={brandRoutes.SETUP.CONNECTORS}
                />
              }
            />
            <Route
              path={sharedRoutes.FIVETRAN_CONNECTION}
              element={
                <AdhocFivetranConnection
                  redirectTo={brandRoutes.SETUP.CONNECTORS}
                />
              }
            />
            <Route
              path={guestRoutes.CONFIRM_EMAIL}
              element={<ConfirmEmail />}
            />
            <Route
              path={brandRoutes.CAMPAIGN_MANAGEMENT.ROOT}
              element={withGuard(
                <SubscriptionGuard>
                  <BudgetRecommender />
                </SubscriptionGuard>,
                [isAccessGranted],
              )}
            />
            {/* Asset Library + AdForge routes removed — all asset workflows
                now happen inside AdForge's selected-assets drawer. */}
            <Route
              path={brandRoutes.MARKET_PREDICTION.ROOT}
              element={withGuard(
                <SubscriptionGuard>
                  <MarketPredictionPage />
                </SubscriptionGuard>,
                [isAccessGranted, hasMarketPredictionAccess],
              )}
            />
            <Route
              path={brandRoutes.CAMPAIGN_BLASTER.ROOT}
              element={withGuard(
                <SubscriptionGuard>
                  <CampaignBlasterPage />
                </SubscriptionGuard>,
                [isAccessGranted, hasAdForgeAccess],
              )}
            />
            <Route
              // Legacy /ad-forge entry — AdForge now lives under AdBlaster as
              // /ad-blaster/ad-forge. Redirect preserves PostDetails /
              // AdDetails "blast in AdForge" shortcuts and any user bookmarks.
              path={brandRoutes.AD_FORGE.ROOT}
              element={
                <Navigate to={brandRoutes.AD_BLASTER.AD_FORGE} replace />
              }
            />
            <Route
              // AdBlasterPage owns its own <Routes> for the Creatives/Ads/AdForge
              // sub-paths, so we declare the parent with `/*` to delegate nested
              // segments instead of matching only `/ad-blaster` exactly.
              path={`${brandRoutes.AD_BLASTER.ROOT}/*`}
              element={withGuard(
                <SubscriptionGuard>
                  <AdBlasterPage />
                </SubscriptionGuard>,
                [isAccessGranted, hasAdForgeAccess],
              )}
            />
            <Route
              // Jobs surface AdForge/Ad Blaster background work, so gate the
              // route behind the same access as Ad Blaster — without this the
              // sidebar entry is hidden but /jobs is still directly reachable.
              path={brandRoutes.JOBS.ROOT}
              element={withGuard(
                <SubscriptionGuard>
                  <JobsPage />
                </SubscriptionGuard>,
                [isAccessGranted, hasAdForgeAccess],
              )}
            />
            <Route
              // Comment moderation (MD-5576): reactions, flagged / hidden
              // comments and unhide for the brand's paid Meta posts. Behind
              // its own PostHog flag — the sidebar entry uses the same flag,
              // so nav and route agree.
              path={brandRoutes.COMMENT_MODERATION.ROOT}
              element={withGuard(
                <SubscriptionGuard>
                  <CommentModerationPage />
                </SubscriptionGuard>,
                [isAccessGranted, hasCommentModerationAccess],
              )}
            />
            <Route
              // Live Meta campaign budget editor (no Cube). Behind the
              // `meta-budget-editor` flag, same as its sidebar entry.
              path={brandRoutes.META_BUDGETS.ROOT}
              element={withGuard(
                <SubscriptionGuard>
                  <MetaBudgetsPage />
                </SubscriptionGuard>,
                [isAccessGranted, hasMetaBudgetEditorAccess],
              )}
            />
            <Route
              // Cross-company schedule management. Super admins only — the
              // /jobs/admin/* endpoints enforce the same AUTH_SUPER_ADMIN
              // scope, this guard just keeps the route unreachable for others.
              path={brandRoutes.ADMIN_SCHEDULED_JOBS.ROOT}
              element={withGuard(
                <AdminSchedulesPage />,
                isCompanyManagementGranted,
              )}
            />
            <Route
              // Cross-company launch tracker read from the ad-blast audit
              // history. Super admins only — /ad-blast-reports enforces the
              // same AUTH_SUPER_ADMIN scope server-side.
              path={brandRoutes.ADMIN_AD_BLAST_REPORTS.ROOT}
              element={withGuard(
                <AdBlastReportsPage />,
                isCompanyManagementGranted,
              )}
            />
            <Route
              // Brand Agenda plans/links Ad Blaster + AdForge work, so it is
              // gated behind the same feature flag as the rest of the Ad
              // Creation group — without it the sidebar entry is hidden but
              // the route would still be directly reachable.
              path={brandRoutes.BRAND_AGENDA.ROOT}
              element={withGuard(
                <SubscriptionGuard>
                  <BrandAgendaPage />
                </SubscriptionGuard>,
                [isAccessGranted, hasAdForgeAccess],
              )}
            />
            <Route
              // Creative Management runs Meta ad archival playbooks. Gated by
              // its own feature flag — the sidebar entry uses the same flag,
              // so nav and route agree.
              path={brandRoutes.CREATIVE_MANAGEMENT.ROOT}
              element={withGuard(
                <SubscriptionGuard>
                  <CreativeManagementPage />
                </SubscriptionGuard>,
                [isAccessGranted, hasCreativeManagementAccess],
              )}
            />
            <Route path={brandRoutes.DOCS} element={<Docs />} />
            <Route path="*" element={<Navigate to={defaultRoute} replace />} />
          </Routes>
        </WithDrawer>
      </div>

      {hasChatbotAccess && <ChatButton />}

      {showMoneyWarnDialog && (
        <MoneyWarnDailog onClose={hideMoneyWarnDialog as () => void} />
      )}
    </div>
  )
}
